/* Une ligne de devis se compte en cartons.
 *
 * On ne vend pas trente-sept pots : on vend trois cartons de douze. Le carton
 * est l'unité de commande réelle, l'unité n'en est que la conséquence — et
 * c'est dans ce sens-là qu'il faut calculer, sinon on obtient des quantités
 * que l'usine ne sait pas préparer.
 *
 *   unités = cartons × par_carton
 *   total  = unités × prix unitaire
 *
 * Le prix reste au carton près : il porte sur l'unité, parce que c'est lui
 * qu'on négocie et lui que le plancher de l'agent contrôle. Multiplier par le
 * contenu du carton est la dernière opération, pas la première.
 */

/* Postgres calcule le total de la ligne en numérique exact et arrondit au
   centime supérieur à mi-chemin. Le flottant du navigateur, lui, range 21,105
   juste en dessous et rendrait 21,10 là où le document dirait 21,11. Repasser
   par une précision décimale avant d'arrondir fait dire la même chose aux
   deux — un écran qui annonce un centime de moins que la facture, c'est une
   question de trop à chaque devis. */
function auCentime(x: number): number {
  return Math.round(Number((x * 100).toPrecision(12))) / 100;
}

/** Ce que contient un carton quand la fiche produit ne le dit pas. */
export const PAR_CARTON_INCONNU = 1;

/** Le contenu d'un carton, ramené à quelque chose d'utilisable. */
export function parCarton(valeur: number | null | undefined): number {
  const n = Number(valeur);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : PAR_CARTON_INCONNU;
}

/**
 * Les unités que représentent ces cartons.
 *
 * Les cartons se comptent entiers : un demi-carton ne se commande pas, et
 * laisser passer 2,5 produirait une quantité que personne ne sait charger.
 */
export function unites(cartons: number, contenu: number | null | undefined): number {
  const c = Number.isFinite(cartons) && cartons > 0 ? Math.floor(cartons) : 0;
  return c * parCarton(contenu);
}

/** Le montant d'une ligne, arrondi au centime une seule fois, à la fin. */
export function totalLigne(
  cartons: number, contenu: number | null | undefined, prix: number,
): number {
  const p = Number.isFinite(prix) && prix > 0 ? prix : 0;
  return auCentime(unites(cartons, contenu) * p);
}

/** Ce qu'une ligne annonce en clair : « 3 cartons de 12 — 36 unités ». */
export function phraseCartons(cartons: number, contenu: number | null | undefined): string {
  const c = Math.max(0, Math.floor(cartons || 0));
  const par = parCarton(contenu);
  if (par === PAR_CARTON_INCONNU) {
    return `${c} unité${c > 1 ? "s" : ""}`;
  }
  return `${c} carton${c > 1 ? "s" : ""} de ${par} — ${unites(c, par)} unités`;
}
